import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import BookingFlowPage from '@/features/booking-flow/BookingFlowPage';
import { BOOKING_FLOW_STEP_SLUGS } from '@/features/booking-flow/constants';
import { deriveStepSelection } from '@/features/booking-flow/deriveStepSelection';
import { useCalendarOffersEmployeePick } from '@/features/booking-flow/useCalendarOffersEmployeePick';
import { appendPickedService } from '@/features/booking-flow/appendPickedService';
import { buildTargetLocation, goToStep } from '@/features/booking-flow/goToStep';
import { removePickedService } from '@/features/booking-flow/removePickedService';
import { getNextStep, getStepGuardTarget } from '@/features/booking-flow/steps';
import {
  addServiceTrackingHandlers,
  serviceDetailsTrackingHandlers,
  trackAddOnAdded,
  trackEmployeeSelected,
  trackMainServiceAdded,
} from '@/features/booking-flow/tracking';
import { useBookingFlowSelection } from '@/features/booking-flow/useBookingFlowSelection';
import { parseSelection, readPickedIds, serialiseSelection } from '@/features/booking-flow/urlState';
import withBookingFlowRedesignFlag from '@/features/booking-flow/withBookingFlowRedesignFlag';
import SelectionDrawer from '@/features/booking-selection/components/SelectionDrawer';
import { useSelectionDrawerEmployee } from '@/features/booking-selection/components/SelectionDrawer.hooks';
import SelectionsBar from '@/features/booking-selection/components/SelectionsBar';
import { useSelectionsBar } from '@/features/booking-selection/components/SelectionsBar.hooks';
import SelectionSummary from '@/features/booking-selection/components/SelectionSummary';
import { ANY_EMPLOYEE_ID } from '@/features/booking-selection/constants';
import { buildBookSelectionSummary } from '@/features/booking-selection/selectionSummary.helpers';
import SelectAddOns from '@/features/booking-selection/views/SelectAddOns';
import { useSelectAddOns } from '@/features/booking-selection/views/SelectAddOns.hooks';
import { useSelectService } from '@/features/booking-selection/views/SelectService.hooks';
import { useAppSelector } from '@/hooks';
import { _t } from '@/locale';
import { BookState } from '@/types/state/book';
import { useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

/**
 * The `/tillval` step page (U12): mounts the built-but-unwired `SelectAddOns` view inside
 * the flow shell, with the "Valda tjänster / Totalt / Sök tider" summary as the desktop
 * side column (KTD8) and the SelectionsBar as the mobile sticky footer.
 *
 * All selection changes go through the URL (R2): adding an add-on pushes an `rs` patch to
 * this same step, removing one pushes the remaining `rs`, and the hydration hook re-derives
 * `state.book` from the changed search. The page never dispatches selection changes itself.
 */
const AddOnsPage = () => {
  const { placeSlugId } = useParams<{ placeSlugId: string }>();
  const history = useHistory();
  const location = useLocation();
  const book = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const place = book.place;
  useBookingFlowSelection(place);
  const calendarOffersEmployeePick = useCalendarOffersEmployeePick();
  const { attribution } = useBookingFlowChrome();
  // Deliberately local, not in the URL: the drawer sits *over* this step rather than being
  // one, so it must not be deep-linkable or restorable.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { addOnIds: pickedAddOnIds, mainServiceIds: pickedMainServiceIds } = readPickedIds(location.search);

  const appendAddOn = (serviceId: number, track: (serviceIds: string[]) => void) =>
    appendPickedService(history, placeSlugId, BOOKING_FLOW_STEP_SLUGS.ADD_ONS, 'rs', serviceId, {
      hydratedServices: book.services,
      track,
    });
  // The page list is the modal's inline recommended rows: add_recommended_service. The same
  // rows inside the drawer are a list the visitor opened, so that pick is the add-service
  // funnel's success leg instead, paired with the initiated the drawer reports on open.
  const onPickService = (serviceId: number) => appendAddOn(serviceId, trackAddOnAdded);
  const onPickDrawerAddOn = (serviceId: number) => appendAddOn(serviceId, trackMainServiceAdded);
  const onRemove = (serviceId: number) =>
    removePickedService(history, placeSlugId, BOOKING_FLOW_STEP_SLUGS.ADD_ONS, serviceId, {
      hydratedServices: book.services,
      pickedAddOnIds,
      pickedMainServiceIds,
    });
  const onSearchTimes = () => {
    if (!place) return;
    const selection = deriveStepSelection(place, location.search, calendarOffersEmployeePick, user);
    const target = getNextStep(BOOKING_FLOW_STEP_SLUGS.ADD_ONS, selection);
    // Pin the exit to the first main in `ss` — the service the visitor entered with —
    // since resolveMainServicesFromUrl returns catalog order, not pick order.
    if (target.type === 'calendar') {
      const firstMain = (selection.services ?? []).find((service) => String(service.id) === pickedMainServiceIds[0]);
      goToStep(history, placeSlugId, firstMain ? { ...target, mainService: firstMain } : target, {});
      return;
    }
    goToStep(history, placeSlugId, target, {});
  };

  // Appends to `ss` instead of replacing it, unlike /valj-tjanst — the drawer reaches this
  // handler from "Lägg till fler tjänster".
  const onAddMainService = (serviceId: number) =>
    appendPickedService(history, placeSlugId, BOOKING_FLOW_STEP_SLUGS.ADD_ONS, 'ss', serviceId, {
      hydratedServices: book.services,
      track: trackMainServiceAdded,
    });

  // Hooks run unconditionally (before the guards below can return); all four tolerate an
  // absent place and empty selection.
  const viewProps = useSelectAddOns({
    onPickService,
    ...serviceDetailsTrackingHandlers,
  });
  const addServiceProps = useSelectService({
    onPickService: onAddMainService,
    ...serviceDetailsTrackingHandlers,
  });
  const barProps = useSelectionsBar('Search', { onSearchTimes, onEdit: () => setIsDrawerOpen(true) });
  const {
    employee: drawerEmployee,
    employees: drawerEmployees,
    selectedEmployeeId: drawerSelectedEmployeeId,
    getSrToken: getDrawerEmployeeSrToken,
  } = useSelectionDrawerEmployee();

  if (!place) {
    // useBookingFlowSelection owns the bounded wait and the eventual redirect; this is
    // the same shell-spinner treatment the flag HOC uses while unresolved.
    return (
      <BookingFlowPage title="">
        <LoadingPlaceHolder />
      </BookingFlowPage>
    );
  }

  const guardTarget = getStepGuardTarget(
    BOOKING_FLOW_STEP_SLUGS.ADD_ONS,
    deriveStepSelection(place, location.search, calendarOffersEmployeePick, user),
  );
  if (guardTarget) {
    return <Redirect to={buildTargetLocation(placeSlugId, guardTarget, location.search)} />;
  }

  // Summary rows read hydrated state.book — the display layer, shared with SelectionsBar.
  const pickedServices = book.services ?? [];
  const { items: summaryItems, total } = buildBookSelectionSummary(book, place);

  // Replace, not push: a pushed entry would let browser-back re-enter the flow with a stale
  // employee. `state` must ride along, or the header button reads "Avbryt" instead of back.
  const onPickDrawerEmployee = (employeeId: number) => {
    const sr = getDrawerEmployeeSrToken(employeeId);
    if (!sr) return;
    // Live read: re-picking the already-selected row must not re-emit or re-replace.
    if (parseSelection(history.location.search).sr === sr) return;
    // Modal parity (U15): the employee change is a selection event, like the modal's
    // handleOnPickEmployee.
    trackEmployeeSelected(employeeId === ANY_EMPLOYEE_ID ? 'any' : 'specific');
    history.replace({
      pathname: location.pathname,
      search: serialiseSelection(location.search, { sr }),
      state: location.state,
    });
  };

  // "Lägg till tjänst" offers only main services: resolveMainServicesFromUrl drops add-on
  // ids from `ss`, so an add-on row here would be a silent no-op pick.
  const addOnServiceIds = new Set(
    (place.services ?? [])
      .flatMap((category) => category.services ?? [])
      .filter((service) => service.about?.settings?.isAddOn)
      .map((service) => service.id),
  );
  const drawerAddService = {
    ...addServiceProps,
    categories: addServiceProps.categories
      .map((category) => ({
        ...category,
        services: category.services.filter((service) => !addOnServiceIds.has(service.id)),
      }))
      .filter((category) => category.services.length > 0),
  };

  return (
    <BookingFlowPage
      title={_t('features.bookingSelection.SelectAddOns.title')}
      subtitle={place.about?.name}
      merchantLogoUrl={place.about?.profileImage}
      emitStepShown
      sideColumn={
        <>
          <SelectionSummary items={summaryItems} total={total} onRemove={onRemove} onSearchTimes={onSearchTimes} />
          {attribution ? <div className="mt-sm">{attribution('column')}</div> : null}
        </>
      }
      bottomBar={<SelectionsBar {...barProps} />}>
      <SelectAddOns {...viewProps} />
      {/* Below `lg` the side column is hidden, so the list carries the attribution instead. */}
      {attribution ? <div className="mt-sm lg:hidden">{attribution('column')}</div> : null}
      {/* Mobile-only by reachability, not CSS: BottomSheetModal portals to document.body, so
          only the SelectionsBar edit button (itself `lg:hidden`) can ever open it. */}
      <SelectionDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        employee={drawerEmployee}
        summary={{ items: summaryItems, total, onRemove, onSearchTimes }}
        recommended={{ ...viewProps, onPickService: onPickDrawerAddOn }}
        {...addServiceTrackingHandlers}
        // The modal's rule (SummaryView's `showAddAdditionalService: !pickedAddonServices.length`):
        // once an add-on is picked, adding another main service is no longer offered.
        addService={pickedServices.some((service) => service?.about?.settings?.isAddOn) ? undefined : drawerAddService}
        employeeList={{
          employees: drawerEmployees,
          selectedEmployeeId: drawerSelectedEmployeeId,
          onPick: onPickDrawerEmployee,
        }}
      />
    </BookingFlowPage>
  );
};

export default withBookingFlowRedesignFlag(AddOnsPage);
